import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import PublicAgentAuthForm from "@/components/agent/public/PublicAgentAuthForm";

const PublicAgentAuth = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embed') === 'true';
  const [agent, setAgent] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchAgentAndOrganization = async () => {
      console.log("PublicAgentAuth: Received publicId parameter:", publicId);
      
      if (!publicId) {
        console.error("PublicAgentAuth: Missing publicId parameter in URL");
        setError("No agent ID provided");
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching agent with public_id:", publicId);
        const { data: agentData, error: agentError } = await supabase
          .from("agents")
          .select("*, organization_id")
          .eq("public_id", publicId)
          .eq("is_active", true)
          .maybeSingle();

        if (agentError) {
          console.error("Error fetching agent:", agentError);
          throw new Error("Agent not found");
        }

        if (!agentData) {
          console.error("No agent found with public_id:", publicId);
          throw new Error("Agent not found or inactive");
        }

        console.log("Found agent:", agentData);
        setAgent(agentData);

        if (agentData.organization_id) {
          try {
            console.log("Fetching organization with ID:", agentData.organization_id);
            const { data: orgData, error: orgError } = await supabase
              .rpc('get_organization_by_id', { org_id: agentData.organization_id });

            if (orgError) {
              console.error("Error fetching organization:", orgError);
            } else if (orgData && orgData.length > 0) {
              console.log("Found organization:", orgData[0]);
              setOrganization(orgData[0]);
            } else {
              console.warn("Organization not found for id:", agentData.organization_id);
            }
          } catch (orgErr) {
            console.error("Exception fetching organization:", orgErr);
          }
        }
      } catch (err: any) {
        console.error("Error in fetchAgentAndOrganization:", err);
        setError(err.message || "Failed to load the requested agent");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentAndOrganization();
  }, [publicId]);

  const handleSubmit = async (data: { name: string; email: string; phone?: string }) => {
    try {
      console.log("Saving user data:", data);
      
      // Get phone number requirement from agent
      const requirePhone = agent?.contact_info?.requirePhoneNumber === true;
      
      // Validate phone if required
      if (requirePhone && !data.phone) {
        toast({
          variant: "destructive",
          title: "Phone Required",
          description: "Please provide a phone number to continue."
        });
        return;
      }
      
      // Save user info to localStorage for the chat page to use
      localStorage.setItem(`agent_chat_user_${publicId}`, JSON.stringify({
        name: data.name.trim() || "Anonymous User",
        email: data.email.trim() || undefined,
        phone: data.phone || undefined
      }));
      
      // Also create or update the agent_chat_user in the database
      try {
        // First get the existing user ID if available
        const existingUserId = localStorage.getItem(`agent_chat_user_id_${publicId}`);
        
        if (existingUserId) {
          // Update existing record
          const { error } = await supabase
            .from("agent_chat_users")
            .update({
              name: data.name.trim() || "Anonymous User",
              email: data.email.trim() || null,
              phone: data.phone || null,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingUserId);
            
          if (error) throw error;
        } else {
          // Create new record
          const { data: newUser, error } = await supabase
            .from("agent_chat_users")
            .insert({
              name: data.name.trim() || "Anonymous User",
              email: data.email.trim() || null,
              phone: data.phone || null,
              is_anonymous: true,
            })
            .select()
            .single();
            
          if (error) throw error;
          
          // Save the user ID for future use
          if (newUser?.id) {
            localStorage.setItem(`agent_chat_user_id_${publicId}`, newUser.id);
          }
        }
      } catch (dbError) {
        console.error("Error saving user to database:", dbError);
        // Continue anyway since we at least have localStorage
      }
      
      // Redirect to chat page with embed parameter if needed
      const redirectUrl = `/${encodeURIComponent(organization?.agent_url)}/${publicId}${isEmbedded ? '?embed=true' : ''}`;
      navigate(redirectUrl);
    } catch (error: any) {
      console.error("Error saving user information:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save your information. Please try again."
      });
    }
  };

  const handleClose = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.close();
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md mx-auto text-center space-y-4">
          <h1 className="text-2xl font-bold">Agent Not Found</h1>
          <p className="text-muted-foreground">
            {error || "The agent you're looking for doesn't exist or has been deactivated."}
          </p>
          <button 
            onClick={() => navigate("/")}
            className="bg-[#060520] text-white px-4 py-2 rounded hover:bg-[#060520]/90"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const brandColor = organization?.brand_color || "#060520";

  return (
    <div className="min-h-screen w-full flex flex-col overflow-hidden">
      <div className="bg-white w-full h-full flex flex-col overflow-hidden">
        <PublicAgentAuthForm 
          onSubmit={handleSubmit}
          agent={agent}
          organization={organization}
          isEmbedded={isEmbedded}
          onClose={handleClose}
        />
      </div>
    </div>
  );
};

export default PublicAgentAuth;
